import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { RefreshCw, EyeOff, Eye, Search, Gamepad2, ExternalLink } from "lucide-react";
import api, { errMsg } from "@/lib/api";

const Stat = ({ label, value, testId }) => (
  <div data-testid={testId} className="bg-black/30 border border-zinc-800 rounded-lg px-3 py-2">
    <div className="text-[11px] uppercase tracking-wider text-zinc-500">{label}</div>
    <div className="font-head text-xl font-semibold">{value ?? "—"}</div>
  </div>
);

const RefreshBox = ({ meta, onStarted }) => {
  const [src, setSrc] = useState(meta?.source_url || "");
  const [live, setLive] = useState(true);
  const run = async () => {
    try { await api.post("/admin/games/refresh", { source_url: src, live_check: live }); toast.success("Refresh started — this can take a few minutes"); onStarted(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex flex-wrap items-end gap-3">
      <label className="flex-1 min-w-[260px]">
        <span className="text-xs uppercase tracking-wider text-zinc-500">README source URL (HTTPS, raw markdown)</span>
        <input data-testid="games-source-url" value={src} onChange={(e) => setSrc(e.target.value)} placeholder="https://raw.githubusercontent.com/…/README.md"
          className="mt-1 w-full bg-black border border-zinc-800 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:border-volt" />
      </label>
      <label className="flex items-center gap-2 text-sm text-zinc-400 pb-2"><input type="checkbox" data-testid="games-live-check" checked={live} onChange={(e) => setLive(e.target.checked)} /> Check every link</label>
      <button data-testid="games-refresh" onClick={run} disabled={meta?.status === "running"}
        className="inline-flex items-center gap-2 bg-volt text-black font-medium text-sm rounded-md px-4 py-2 disabled:opacity-50">
        <RefreshCw className={`w-4 h-4 ${meta?.status === "running" ? "animate-spin" : ""}`} /> {meta?.status === "running" ? "Refreshing…" : "Refresh game catalogue"}
      </button>
    </div>
  );
};

const Domains = ({ meta, onSaved }) => {
  const [text, setText] = useState((meta?.hidden_domains || []).join("\n"));
  const save = async () => {
    try { await api.put("/admin/games/hidden-domains", { domains: text.split(/[\s,]+/).filter(Boolean) }); toast.success("Hidden domains saved"); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <span className="text-xs uppercase tracking-wider text-zinc-500">Hidden domains (one per line — hides every game on that site)</span>
      <textarea data-testid="games-hidden-domains" value={text} onChange={(e) => setText(e.target.value)} rows={3}
        className="mt-1 w-full bg-black border border-zinc-800 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:border-volt" />
      <button data-testid="games-save-domains" onClick={save} className="mt-2 text-sm bg-zinc-800 hover:bg-zinc-700 rounded-md px-3 py-1.5">Save hidden domains</button>
    </div>
  );
};

export const AdminGames = () => {
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [hiddenOnly, setHiddenOnly] = useState(false);
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    api.get("/admin/games", { params: { search: q, page, hidden_only: hiddenOnly } }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [q, page, hiddenOnly]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (data?.meta?.status !== "running") return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [data?.meta?.status, load]);
  const toggle = async (g) => {
    try { await api.put(`/admin/games/${g.id}/hidden`, { hidden: !g.hidden }); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const m = data?.meta || {};
  return (
    <div data-testid="admin-games-panel" className="space-y-6">
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 space-y-4">
        <div className="flex items-center gap-2"><Gamepad2 className="w-5 h-5 text-volt" /><h3 className="font-head font-semibold">/game catalogue</h3>
          <span data-testid="games-status" className={`ml-2 text-xs rounded-full px-2 py-0.5 ${m.status === "failed" ? "bg-red-500/15 text-red-400" : m.status === "running" ? "bg-amber-500/15 text-amber-300" : "bg-emerald-500/15 text-emerald-300"}`}>{m.status || "not imported"}</span>
          {m.imported_at && <span className="text-xs text-zinc-500">last import {new Date(m.imported_at).toLocaleString()}</span>}</div>
        {m.error && <p data-testid="games-error" className="text-sm text-red-400">Last refresh failed: {m.error} — the previous catalogue is still live.</p>}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <Stat testId="games-stat-playable" label="Playable" value={m.playable} />
          <Stat testId="games-stat-rows" label="Table rows" value={m.table_rows} />
          <Stat testId="games-stat-source" label="Source-only" value={m.source_only} />
          <Stat testId="games-stat-invalid" label="Invalid URLs" value={m.invalid_urls} />
          <Stat testId="games-stat-dead" label="Dead links" value={m.dead_links} />
          <Stat testId="games-stat-dupes" label="Duplicates" value={m.duplicates} />
        </div>
        {data && <RefreshBox meta={m} onStarted={load} />}
        {data && <Domains key={(m.hidden_domains || []).join()} meta={m} onSaved={load} />}
      </div>
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 min-w-[220px]"><Search className="w-4 h-4 absolute left-3 top-2.5 text-zinc-500" />
            <input data-testid="games-search" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search title or domain…"
              className="w-full bg-black border border-zinc-800 rounded-md pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-volt" /></div>
          <label className="flex items-center gap-2 text-sm text-zinc-400"><input type="checkbox" data-testid="games-hidden-only" checked={hiddenOnly} onChange={(e) => { setHiddenOnly(e.target.checked); setPage(1); }} /> Hidden only</label>
          <span className="text-xs text-zinc-500">{data?.total ?? 0} games</span>
        </div>
        <div className="divide-y divide-zinc-800" data-testid="games-list">
          {(data?.games || []).map((g) => (
            <div key={g.id} data-testid={`games-row-${g.id}`} className={`flex items-center gap-3 py-2 text-sm ${g.hidden ? "opacity-50" : ""}`}>
              <span className="flex-1 min-w-0 truncate">{g.title}</span>
              <a href={g.url} target="_blank" rel="noopener noreferrer nofollow" className="text-xs text-zinc-500 hover:text-zinc-300 flex items-center gap-1 shrink-0">{g.host}<ExternalLink className="w-3 h-3" /></a>
              <button data-testid={`games-toggle-${g.id}`} onClick={() => toggle(g)} className="shrink-0 text-xs flex items-center gap-1 bg-zinc-800 hover:bg-zinc-700 rounded-md px-2 py-1">
                {g.hidden ? <><Eye className="w-3 h-3" /> Unhide</> : <><EyeOff className="w-3 h-3" /> Hide</>}</button>
            </div>
          ))}
        </div>
        {data && data.pages > 1 && (
          <div className="flex items-center justify-end gap-2 mt-4 text-sm">
            <button data-testid="games-prev" disabled={page <= 1} onClick={() => setPage(page - 1)} className="bg-zinc-800 rounded-md px-3 py-1 disabled:opacity-40">Prev</button>
            <span className="text-zinc-500">{page}/{data.pages}</span>
            <button data-testid="games-next" disabled={page >= data.pages} onClick={() => setPage(page + 1)} className="bg-zinc-800 rounded-md px-3 py-1 disabled:opacity-40">Next</button>
          </div>
        )}
      </div>
    </div>
  );
};
